import { useState, useEffect } from "react";
import Slct from "../Slct";
import api from "../../services/api";
import { toast } from "react-toastify";
import useWrapper from "../../hooks/useWrapper";
import { Currency, File } from "../Input";
import Button from "../Button";
import DatePicker from "../DatePicker";
import ModalCustom from "./modal-template-default";
import Required from "../Required";
import { validateForm } from "../ValidateForm";
import useFormat from "../../hooks/utils/useFormat";
import Results from "../Results";
import usePermission from "../../hooks/usePermission";
import { uploadFile } from "../../hooks/api/uploadFile";
import useSuppliers from "../../hooks/api/useSuppliers";
import QRCode from "qrcode";
import IcDuplicar from "../Svg/IcDuplicar";
import { buildPixPayload } from "../../util/pixBrCode";
import {
  PAYMENT_METHOD_OPTIONS,
  PIX_KEY_TYPE_LABEL,
  ACCOUNT_TYPE_LABEL,
  ACCOUNT_OWNERSHIP_LABEL,
  formatPixKey,
  formatDocument,
  supplierAccountOptions,
  supplierPixOptions,
  withEmptyOption,
  NO_ACCOUNTS_LABEL,
  NO_PIX_KEYS_LABEL,
} from "../../util/paymentMethods";

/**
 * Baixa manual de uma parcela de pagamento (bills to pay).
 * Registra data do pagamento + valor pago (editável) + comprovante opcional.
 *
 * Mostra também para onde depositar, seguindo o método escolhido aqui e não o
 * do título: o gestor pode pagar por outro meio na hora. A lista vem do
 * cadastro atual do fornecedor (ele pode ter trocado de banco depois que o
 * título foi criado) e a escolha fica registrada na parcela.
 */
const ModalBillToPayBaixa = props => {
  const { show, onHide, bill, negotiation, refreshCallback } = props;
  const { config, token } = useWrapper();
  const { formatReal } = useFormat();
  const canPaymentDown = usePermission("financialCompany", "paymentDown");

  const suppliers = useSuppliers();

  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [valuePaid, setValuePaid] = useState();
  const [fileToUpload, setFileToUpload] = useState();
  const [accountId, setAccountId] = useState(null);
  const [pixId, setPixId] = useState(null);
  const [isWaitingResponse, setisWaitingResponse] = useState(false);

  useEffect(() => {
    if (show) {
      suppliers.load();
      setPaymentMethod(bill?.method_payment || "");
      setPaymentDate(new Date());
      setValuePaid(bill?.value);
      setFileToUpload();
      // parte do que o título combinou; se o gestor trocar o método, o campo
      // correspondente é limpo no onChange
      setAccountId(negotiation?.payment_account_id || null);
      setPixId(negotiation?.payment_pix_id || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, bill]);

  const supplier =
    negotiation?.payment_for === "empresa"
      ? suppliers?.data?.find(__supplier => __supplier.id === negotiation?.provider_id)
      : null;
  const accountOptions = supplierAccountOptions(supplier);
  const pixOptions = supplierPixOptions(supplier);
  const selectedAccount = supplier?.account?.find(__account => __account.id === accountId);
  const effectivePixId = pixId || (pixOptions.length === 1 ? pixOptions[0].value : null);
  const selectedPix = supplier?.pixKeys?.find(__pix => __pix.id === effectivePixId);
  // o select aparece mesmo sem nada cadastrado, com a opção desabilitada
  // explicando — esconder faria parecer que a tela está quebrada
  const showAccounts = paymentMethod === "transferencia";
  const showPix = paymentMethod === "pix";

  // "PIX copia e cola" da chave escolhida, já com o valor que está sendo pago;
  // vira QR ao lado dos dados. Sem titular ou cidade o padrão não permite gerar
  // um código válido, então nada é exibido em vez de um QR que o banco recusa.
  const pixPayload = showPix
    ? buildPixPayload({
        keyType: selectedPix?.key_type,
        key: selectedPix?.pix_key,
        merchantName: selectedPix?.name || supplier?.name,
        merchantCity: supplier?.city?.name,
        amount: valuePaid,
      })
    : null;

  const [pixQrCode, setPixQrCode] = useState(null);
  useEffect(() => {
    if (!pixPayload) {
      setPixQrCode(null);
      return;
    }
    let _active = true;
    QRCode.toDataURL(pixPayload, { margin: 1, width: 240 })
      .then(__url => _active && setPixQrCode(__url))
      .catch(__error => {
        console.log("__error", __error);
        _active && setPixQrCode(null);
      });
    return () => {
      _active = false;
    };
  }, [pixPayload]);

  const copyPixPayload = async () => {
    try {
      await navigator.clipboard.writeText(pixPayload);
      toast.success("Código PIX copiado!");
    } catch (__error) {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
      console.log("__error", __error);
    }
  };

  const closeModal = () => {
    if (isWaitingResponse) return;
    onHide();
  };

  const confirmBaixa = async () => {
    if (!canPaymentDown) return;
    const _is_valid = validateForm(document.getElementById("bill_to_pay_baixa_container"));
    if (!_is_valid) return;
    setisWaitingResponse(true);
    try {
      let _file_id = null;
      if (fileToUpload) {
        const _response = await uploadFile({
          file: fileToUpload,
          key: "proof",
          token,
        });
        _file_id = _response?.data?.id || null;
      }

      await api.put(
        `bills-to-pay/baixa/${bill.id}`,
        {
          payment_date: paymentDate,
          value_paid: valuePaid || 0,
          method_payment: paymentMethod,
          payment_account_id: paymentMethod === "transferencia" ? accountId || null : null,
          payment_pix_id: paymentMethod === "pix" ? effectivePixId : null,
          ...(_file_id ? { file_id: _file_id } : {}),
        },
        config,
      );
      toast.success("Pagamento confirmado!");
      refreshCallback && refreshCallback();
      onHide();
    } catch (__err) {
      const _message = __err?.response?.data?.error?.message;
      toast.error(_message || "Problemas ao registrar a baixa do pagamento!");
      console.error("__err", __err);
    } finally {
      setisWaitingResponse(false);
    }
  };

  return (
    <ModalCustom show={show} onHide={closeModal} id="bill_to_pay_baixa_container" disableClose={isWaitingResponse}>
      <ModalCustom.Header>
        <ModalCustom.Title>Confirmar Pagamento</ModalCustom.Title>
      </ModalCustom.Header>
      <ModalCustom.Body>
        <p className="small-label">
          Você está prestes a registrar a baixa manual de um pagamento. Certifique-se de que todas as informações estão
          corretas.
        </p>
        <div className="mt-2">
          <label>
            Método de Pagamento <Required />
          </label>
          <Slct
            required={true}
            isDisabled={isWaitingResponse}
            value={paymentMethod}
            onChange={__ev => {
              setPaymentMethod(__ev.value);
              // os dados de destino são do método anterior: trocar de método
              // limpa o que não vale mais
              if (__ev.value !== "transferencia") setAccountId(null);
              if (__ev.value !== "pix") setPixId(null);
            }}
            options={PAYMENT_METHOD_OPTIONS}
            placeholder="..."
          />
        </div>
        {showAccounts && (
          <div className="mt-2">
            <label>Conta para depósito</label>
            <Slct
              isDisabled={isWaitingResponse}
              options={withEmptyOption(accountOptions, NO_ACCOUNTS_LABEL)}
              value={accountId}
              onChange={__ev => setAccountId(__ev.value)}
              placeholder="..."
            />
            {!!selectedAccount && (
              <p className="small-label mb-0 mt-2">
                <b>Banco:</b> {selectedAccount.bank}
                <br />
                <b>Agência:</b> {selectedAccount.agency}
                <br />
                <b>Conta:</b> {selectedAccount.account}
                {!!selectedAccount.type && ` (${ACCOUNT_TYPE_LABEL[selectedAccount.type] || selectedAccount.type})`}
                {!!selectedAccount.ownership && (
                  <>
                    <br />
                    <b>Titularidade:</b>{" "}
                    {ACCOUNT_OWNERSHIP_LABEL[selectedAccount.ownership] || selectedAccount.ownership}
                  </>
                )}
                {!!selectedAccount.document && (
                  <>
                    <br />
                    <b>{selectedAccount.ownership === "cpf" ? "CPF" : "CNPJ"}:</b>{" "}
                    {formatDocument(selectedAccount.ownership, selectedAccount.document)}
                  </>
                )}
              </p>
            )}
          </div>
        )}
        {showPix && (
          <div className="mt-2">
            <label>Chave PIX</label>
            <Slct
              isDisabled={isWaitingResponse}
              options={withEmptyOption(pixOptions, NO_PIX_KEYS_LABEL)}
              value={effectivePixId}
              onChange={__ev => setPixId(__ev.value)}
              placeholder="..."
            />
            {!!selectedPix && (
              <div className="d-flex align-items-stretch gap-3 mt-2">
                {!!pixQrCode && (
                  <img className="flex-shrink-0" src={pixQrCode} alt="QR Code do PIX" width={110} height={110} />
                )}
                <div className="d-flex flex-column justify-content-between">
                  <p className="small-label mb-0">
                    <b>{PIX_KEY_TYPE_LABEL[selectedPix.key_type] || "Chave"}:</b>{" "}
                    {formatPixKey(selectedPix.key_type, selectedPix.pix_key)}
                    {!!selectedPix.bank && (
                      <>
                        <br />
                        <b>Banco:</b> {selectedPix.bank}
                      </>
                    )}
                    {!!selectedPix.name && (
                      <>
                        <br />
                        <b>Titular:</b> {selectedPix.name}
                      </>
                    )}
                  </p>
                  {!!pixPayload && (
                    <Button
                      color="grey"
                      size="sm"
                      className="align-self-start d-inline-flex align-items-center gap-2 op-5"
                      onClick={copyPixPayload}
                    >
                      <IcDuplicar /> Copiar código
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt-2">
          <label>
            Data do Pagamento <Required />
          </label>
          <DatePicker
            required={true}
            disabled={isWaitingResponse}
            selected={paymentDate || ""}
            changeDate={__ev => {
              if (__ev) setPaymentDate(__ev);
            }}
          />
        </div>
        <div className="mt-2">
          <label>
            Valor Pago <Required />
          </label>
          <Currency
            required={true}
            disabled={isWaitingResponse}
            allowNegative={false}
            value={valuePaid}
            valueChange={__ev => setValuePaid(__ev)}
          />
        </div>
        <div className="mt-2">
          <label>Comprovante (opcional)</label>
          <File
            file_id={null}
            file_to_upload={fileToUpload}
            setFile={setFileToUpload}
            title="Comprovante de Pagamento"
            name="comprovante"
            disabled={isWaitingResponse}
          />
        </div>
        <div className="mt-4">
          <Results
            rows={[
              {
                label: "Valor da Parcela",
                value: formatReal(bill?.value || 0),
                type: "dark",
              },
              {
                label: <b>Valor Pago</b>,
                value: <b>{formatReal(valuePaid || 0)}</b>,
                type: "dark",
              },
            ]}
          />
        </div>
      </ModalCustom.Body>
      <ModalCustom.Footer>
        <Button disabled={isWaitingResponse} color="primary_ghost" onClick={closeModal}>
          Cancelar
        </Button>
        <Button
          disabled={isWaitingResponse || !canPaymentDown}
          onClick={confirmBaixa}
          loading={isWaitingResponse}
          title={!canPaymentDown ? "Você não tem permissão para realizar essa ação." : undefined}
        >
          Confirmar Pagamento
        </Button>
      </ModalCustom.Footer>
    </ModalCustom>
  );
};

export default ModalBillToPayBaixa;
